'use client';

import type { BusinessQuickCategoryWidgetData } from '@weberus/shared-types';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { useEffect, useState, type FC } from 'react';
import { IcMenu } from '@yanolja-rnd/nol-icons';
import dynamic from 'next/dynamic';
import { clientLogStandardized } from '@weberus/client-tracker';
import { historyBack, waitUntilClickTimeout } from '@weberus/utils';
import { clientLogProtobuf } from '@/app/_shared/utils/client-log';
import { cn } from '@/app/_shared/utils/tw-cn';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { HomePageLog } from '@/app/_shared/protobuf-log/clientlogs/home-common-txtpb';
import config from '@/conf';
import { BusinessQuickCategoryItem } from './business-quick-category-item';
import { CategoryModal } from './category-modal';

const CategoryOverview = dynamic(() => import('@/app/_components/common/category-overview'));

export interface BusinessQuickCategoryProps {
  data: BusinessQuickCategoryWidgetData;
  pageNameLog: HomeLogPageNameValue;
}

const nearByItem = {
  action: {
    app: 'yanoljamotel://subhome?type=MOTEL&modal=true',
    web: `${config.service.home.origin}/around/keyword-motel?advert=AROUND`,
  },
  type: '',
  title: '내주변',
  image: {
    url: 'https://yaimg.yanolja.com/joy/sunny/static/images/ic-nearme.png',
  },
};

/**
 *  위젯_퀵카테고리(공통)
 *
 *  @see {@link https://www.figma.com/design/zlHuiOHmYv1ghVDbTHUSvW/%5BGUIDE%5D-%EC%9C%84%EC%A0%AF%2F%EC%98%A4%EB%8D%94%EB%A7%81?node-id=7-5919&m=dev}
 */
export const BusinessQuickCategoryGrid: FC<BusinessQuickCategoryProps> = ({ data, pageNameLog }) => {
  const { count, max } = data.items.reduce((acc, val) => ({ count: acc.count + val.count, max: acc.max + val.max }), {
    count: 0,
    max: 0,
  });
  const items = data.items.flatMap((group) => group.items);

  if (!items.length) {
    return null;
  }

  return (
    <div
      className={cn('grid content-center gap-y-16', {
        'grid-cols-4 gap-x-12 pc:grid-cols-4 pc:gap-20': max > count,
        'grid-cols-5 gap-x-2 max-[320px]:gap-x-0 pc:grid-cols-5 pc:gap-x-10 pc:gap-y-24': max <= count,
        'pc:grid-cols-8': count >= 8,
        'pc:grid-cols-10': count >= 10,
      })}>
      {items.map((item, idx) => {
        const logMeta = {
          logMeta: item.serverLogMeta,
          widgetPosition: item.serverLogMeta.widgetPosition,
          position: item.serverLogMeta.position,
        };
        return (
          <BusinessQuickCategoryItem
            key={`${item.type}_${idx}`}
            {...item}
            onClick={async (ev) => {
              ev.preventDefault();

              await waitUntilClickTimeout(
                // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
                clientLogProtobuf(HomePageLog.QuickCategoryBoardRowItem.click, logMeta, pageNameLog)(),
              );
              navigateTo(item.action);
            }}
            onImpressed={() => {
              clientLogProtobuf(HomePageLog.QuickCategoryBoardRowItem.impression, logMeta, pageNameLog)();
            }}
          />
        );
      })}
    </div>
  );
};

/**
 * 홈은 기타 컴포넌트가 추가 정의되어 있음 (e.g. 카테고리 전체보기, 내주변)
 *
 * @see {@link https://www.figma.com/design/zlHuiOHmYv1ghVDbTHUSvW/%5BGUIDE%5D-%EC%9C%84%EC%A0%AF%2F%EC%98%A4%EB%8D%94%EB%A7%81?node-id=302-50620&m=dev}
 */
export const BusinessQuickCategory: FC<BusinessQuickCategoryProps> = ({ data, pageNameLog }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isHome = pageNameLog === 'Home';

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const checkHash = () => {
      const hash = window.location.hash.replace('#', '');
      setIsModalOpen(hash === 'leisure-category');
    };

    checkHash();
    window.addEventListener('popstate', checkHash);

    return () => {
      window.removeEventListener('popstate', checkHash);
    };
  }, []);

  return (
    <div className="my-12 flex items-center justify-center pc:my-40 pc:w-pc-size">
      {!!isHome && (
        <button
          aria-label="카테고리 열기"
          type="button"
          className="mr-32 mt-[15.5px] hidden self-start rounded-24 border-[0.05rem] border-solid border-line-neutral-weak3 bg-static-white p-16 shadow-2 active:bg-fill-neutral-weak hover-never:bg-fill-neutral-weak pc:block"
          onClick={() => {
            clientLogStandardized('click', pageNameLog, { object: 'cateOverviewBtn', objectContainer: 'header' });
            setIsOpen(true);
          }}>
          <IcMenu className="size-36" />
        </button>
      )}

      <div className="flex">
        <BusinessQuickCategoryGrid data={data} pageNameLog={pageNameLog} />
        {!!isHome && (
          <div className="hidden pc:flex">
            <div className="mx-10 mt-10 h-89 w-1 bg-line-neutral-weak2" />
            <BusinessQuickCategoryItem
              {...nearByItem}
              onClick={async (ev) => {
                ev.preventDefault();
                await waitUntilClickTimeout(
                  clientLogStandardized('click', pageNameLog, {
                    object: '내주변',
                    objectContainer: 'header',
                  }),
                );
                navigateTo(nearByItem.action);
              }}
            />
          </div>
        )}
      </div>

      {/* 레저 카테고리 */}
      <CategoryModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          window.history.replaceState(null, '', window.location.pathname);

          const leisureSubHome = '/sub-home/leisure';
          window.history.length > 1 ? historyBack(leisureSubHome) : window.location.replace(leisureSubHome);
        }}
      />

      {/* 홈 카테고리 */}
      <CategoryOverview
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
      />
    </div>
  );
};
